import { Button } from '@vmware-react/clarity';
import { Breadcrumb, BreadcrumbUnit } from '@vmware-react/vrlcm';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BreadcrumbUnit as BreadcrumbUnitType } from 'vrlcm-gui-types';
import { l10n } from '../with-app-root/i18n/i18nUtil';
import './PageHeader.css';

interface Props {
  breadcrumbUnits: BreadcrumbUnitType[];
  collapsed?: boolean;
  actions?: JSX.Element;
  showCollapseBtn?: boolean;
  showHeaderBlock?: boolean;
  collapseChange?: (collapsed: boolean) => void;
}

const PageHeader: React.FC<Props> = props => {
  const {
    breadcrumbUnits,
    children,
    actions,
    collapseChange,
    showCollapseBtn = true,
    showHeaderBlock = true
  } = props;
  const [collapsedState, setCollapsedState] = useState(
    props.collapsed || false
  );
  const collapsed = collapsedState || breadcrumbUnits.length === 0;

  const toggleCollapsed = () =>
    setCollapsedState(prevCollapsed => !prevCollapsed);

  const history = useHistory();
  const breadcrumbRef: React.RefObject<HTMLDivElement> = useRef(null);

  const routeToPath = (path: string, redirectFunc?: Function) => () => {
    if (redirectFunc) {
      redirectFunc();
      return;
    }
    history.push(path);
  };

  useEffect(() => {
    if (collapseChange) {
      collapseChange(collapsedState);
    }
  }, [collapsedState, collapseChange]);

  useEffect(() => {
    setTimeout(() => {
      const cur: HTMLElement | null = breadcrumbRef.current;
      cur &&
        (cur.getElementsByClassName(
          'crc-breadcrumb-unit-text'
        )[0] as HTMLElement) &&
        (cur.getElementsByClassName(
          'crc-breadcrumb-unit-text'
        )[0] as HTMLElement).focus();
    }, 100);
  }, []);

  return (
    <div className={`page-header${collapsed ? ' collapsed' : ''}`}>
      <div className={`${collapsed ? 'collapsed' : ''}`} ref={breadcrumbRef}>
        <Breadcrumb>
          {breadcrumbUnits.map((unit, index) => {
            return (
              <BreadcrumbUnit
                key={index}
                onClick={routeToPath(unit.to, unit.redirectFunc)}
              >
                {unit.name}
              </BreadcrumbUnit>
            );
          })}
        </Breadcrumb>
      </div>
      {showHeaderBlock && (
        <div className="header-block">
          <div className="header-container">
            <h1 aria-level={1} className="header-text">
              {children}
            </h1>
            <div className="spacer" />
            {actions}
            {breadcrumbUnits.length > 0 && showCollapseBtn && (
              <span className="collapse-btn-wrapper">
                <Button
                  family="link"
                  size="sm"
                  onClick={toggleCollapsed}
                  attrs={{
                    'aria-label': l10n(
                      'components.pageHeader.toggleBreadcrumbNav'
                    ),
                    'aria-expanded': !collapsed
                  }}
                >
                  <clr-icon
                    title={l10n('components.pageHeader.iconTitle', [
                      collapsed ? l10n('expand') : l10n('collapse')
                    ])}
                    shape="angle-double"
                    className={`${collapsed ? 'collapsed' : ''}`}
                  />
                </Button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PageHeader;
